import Loading from '@/components/Loading';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { updateSettings } from '@/components/UI/Table/Reusable/Cache.utils';
import type { FilterOption, TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useFetchContext } from '@/support/Hooks/fetch';
import { RouteService } from '@/support/RouteService';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  plannedAnimalBulkActions,
  plannedAnimalColumns,
  plannedAnimalFilterOptions,
  PlannedAnimalWithGroup,
} from './PlannedAnimals.utils';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import { notAborted } from '@/support/Hooks/fetch/useAbortController.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { MetadataField } from '@/model/Metadata.model.ts';
import { mapMetadataResponse, MetadataGlossaryItem } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import { useModalAction } from '@/utils/modal.tsx';
import { _isNil, _isNotEmpty, uuid } from '@/littledash.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService.ts';
import { RiAlertFill } from 'react-icons/ri';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { Animal, AnimalDefinitionV1 } from '@/model/Animal.model.ts';
import {
  AnimalCreateV1,
  AnimalDefinitionApiId,
  AnimalDefinitionUpsertV1,
  AnimalMapV1,
} from '@/model/PlannedAnimal.model';
import Button from '@/components/UI/Button';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const featureSelector = createSelector([featuresSelector], (features) => ({
  atunesIntegrationEnabled: features?.atunes_colony_management_integration ?? false,
}));

interface ContextProps {
  entity: Study;
}

interface PlannedAnimalsTableProps {
  plannedAnimals: Array<AnimalDefinitionV1>;
  fetchPlannedAnimals: () => Promise<void>;
}

const checkGroupsValid = (
  groups: Array<TreatmentGroup>,
  plannedAnimals: Array<AnimalDefinitionV1>
): string | undefined => {
  const groupIds = new Set(groups.map((group) => group.api_id as GroupApiId));
  const invalidPlannedAnimals = plannedAnimals.reduce<Array<AnimalDefinitionV1['planned_id']>>((acc, animal) => {
    if (_isNil(animal.study_group_api_id) || !groupIds.has(animal.study_group_api_id)) {
      acc.push(animal.planned_id);
    }
    return acc;
  }, []);
  const availableCapacity = groups.reduce((total, group) => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const availableGroupCapacity = (group.max_subjects || 0) - (group?.animals_count || 0);
    return total + Math.max(0, availableGroupCapacity);
  }, 0);
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const availablePlannedAnimals = plannedAnimals.filter((animal) => !animal.animal_api_id).length;
  if (invalidPlannedAnimals.length > 0) {
    return `Some planned animals are assigned to invalid groups: ${[...new Set(invalidPlannedAnimals)].join(', ')}`;
  }
  if (availableCapacity <= 0 && availablePlannedAnimals > 0) {
    return 'All treatment groups are at full capacity';
  }
  if (availableCapacity < availablePlannedAnimals) {
    return 'Not enough available capacity in treatment groups for all planned animals';
  }
  return undefined;
};

const PlannedAnimalsTable: FC<PlannedAnimalsTableProps> = ({ plannedAnimals, fetchPlannedAnimals }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [columns, setColumns] = useState<Array<TanStackColumnDef<PlannedAnimalWithGroup>>>([]);
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [groupsError, setGroupsError] = useState<string | undefined>();
  const [groupsData, setGroupsData] = useState<Array<TreatmentGroup>>([]);
  const [speciesData, setSpeciesData] = useState<Array<SpeciesV1>>([]);
  const [metadataOptions, setMetadataOptions] = useState<Array<MetadataField>>([]);
  const [animalsData, setAnimalsData] = useState<Array<Animal>>([]);
  const [refetchToken, setRefetchToken] = useState<string>(uuid());
  const [selectedAnimalDefinitions, updateSelectedAnimalDefinitions] = useState<Set<AnimalDefinitionApiId>>(new Set());
  const { entity: study } = useFetchContext<ContextProps>();
  const dispatch = useDispatch();
  const { openModal } = useModalAction();

  const settings = useSelector((state: State) => state.ui.settings);
  const { atunesIntegrationEnabled } = useSelector(featureSelector);
  const unmappedPlannedAnimals: Array<AnimalDefinitionV1> = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return plannedAnimals.filter((animal) => !animal.animal_api_id);
  }, [plannedAnimals]);

  const { invoke: fetchGroups } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: study.id },
    query: { perPage: -1, include: ['animals'] },
    options: { invokeOnInit: false, onError: { slug: 'fetch-study-groups', throw: false } },
  });

  const { invoke: fetchSpecies } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: false,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-study-species' } },
  });

  const { invoke: fetchMetadata } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'subject_meta',
    },
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-study-metadata' } },
  });

  const { invoke: fetchAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: study?.api_id },
    query: { perPage: -1, include: ['study_group', 'cage'] },
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-animals' } },
  });

  const buildColumns = async (
    groupsData: Array<TreatmentGroup>,
    speciesData: Array<SpeciesV1>,
    metadataOptions: Array<MetadataField>,
    animalsData: Array<Animal>
  ) => {
    const columns = plannedAnimalColumns(groupsData, speciesData, metadataOptions, animalsData, study.id);
    setColumns(columns);
    setLoading(false);
  };

  const initialise = async () => {
    try {
      const [groupsResponse, speciesResponse, metadataResponse, animalsResponse] = await Promise.all([
        fetchGroups(),
        fetchSpecies(),
        fetchMetadata(),
        fetchAnimals(),
      ]);
      let groups: Array<TreatmentGroup> = [];
      let species: Array<SpeciesV1> = [];
      let metadata: Array<MetadataField> = [];
      let animals: Array<Animal> = [];
      if (groupsResponse.type === 'success') {
        groups = ((groupsResponse?.body as { data: Array<TreatmentGroup> })?.data ?? []).map((g) => ({
          ...g,
          animals_count: g.animals?.length ?? 0,
        }));
        setGroupsData(groups);
      }
      if (speciesResponse.type === 'success') {
        species = speciesResponse.body.data ?? [];
        setSpeciesData(species);
      }
      if (metadataResponse.type === 'success') {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        metadata = mapMetadataResponse((metadataResponse?.body || []) as Array<MetadataGlossaryItem>);
        setMetadataOptions(metadata);
      }
      if (animalsResponse.type === 'success') {
        animals = (animalsResponse?.body as unknown as { data: Array<Animal> })?.data ?? [];
        setAnimalsData(animals);
      }
      setGroupsError(checkGroupsValid(groups, plannedAnimals));
      setFilterOptions(plannedAnimalFilterOptions(groups, species));
      await buildColumns(groups, species, metadata, animals);
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error);
      }
    }
  };

  useEffect(() => {
    initialise();
  }, [plannedAnimals]);

  const updatedSettings = useMemo(() => {
    return updateSettings('animals', columns, settings);
  }, [columns, settings]);

  const hasAccess = Auth.isWriteUserForStudy(study);
  const bulkActions: (props: UseTableProps<PlannedAnimalWithGroup>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { resetTableState },
    },
  }) => {
    if (hasAccess) {
      const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
      const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;

      const customActions = [
        ...(!atunesIntegrationEnabled
          ? [
              {
                name: 'Create from planned animals',
                action: () => handleCreateFromPlannedAnimals(),
                disabled: hasNoUnmappedPlannedAnimals || hasReviewOutstanding,
                tooltip: hasReviewOutstanding
                  ? 'Pending review must be completed'
                  : hasNoUnmappedPlannedAnimals
                    ? 'Cannot create from planned animals which are already mapped'
                    : undefined,
              },
            ]
          : []),
        {
          name: 'Edit planned animals',
          action: () => handleEditPlannedAnimals(),
          disabled: hasNoUnmappedPlannedAnimals,
          tooltip: hasNoUnmappedPlannedAnimals ? 'Cannot edit planned animals which are already mapped' : undefined,
        },
      ];

      const defaultActions = plannedAnimalBulkActions(
        selectedFlatRows,
        resetTableState,
        study,
        dispatch,
        fetchPlannedAnimals
      );

      return [...customActions, ...defaultActions];
    } else {
      return [];
    }
  };

  const handleCreateFromPlannedAnimals = useCallback(() => {
    openModal('CREATE_ANIMALS', {
      plannedAnimals,
      selectedIds: selectedAnimalDefinitions,
      studyId: study?.id,
      groups: groupsData,
      species: speciesData,
      studyMetadata: metadataOptions,
      onSubmit: async (animals: AnimalCreateV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/create-animals',
            path: { studyApiId: study?.api_id },
            body: animals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully created animals');
          await fetchPlannedAnimals?.();
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not create animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not create animals', {
              cause: err,
              slug: 'create-from-planned-animals',
            })
          );
        }
      },
      editing: false,
    });
  }, [selectedAnimalDefinitions, plannedAnimals, study, groupsData, speciesData, metadataOptions, fetchPlannedAnimals]);

  const handleEditPlannedAnimals = useCallback(() => {
    openModal('CREATE_ANIMALS', {
      plannedAnimals,
      selectedIds: selectedAnimalDefinitions,
      groups: groupsData,
      species: speciesData,
      studyMetadata: metadataOptions,
      onSubmit: async (plannedAnimals: Array<AnimalDefinitionUpsertV1>) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/upsert',
            path: { studyApiId: study?.api_id },
            body: plannedAnimals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully updated planned animals');
          await fetchPlannedAnimals?.();
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not edit planned animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not edit planned animals', {
              cause: err,
              slug: 'edit-planned-animals',
            })
          );
        }
      },
      editing: true,
    });
  }, [selectedAnimalDefinitions, plannedAnimals, groupsData, speciesData, metadataOptions, study, fetchPlannedAnimals]);

  const onRowSelectionChange = useCallback((selectedRows: Array<unknown>) => {
    updateSelectedAnimalDefinitions(
      new Set((selectedRows as Array<{ original: AnimalDefinitionV1 }>)?.map((row) => row.original.api_id) ?? [])
    );
  }, []);

  const handleMapPlannedAnimals = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const animals = animalsData.filter((animal) => !animal.animal_definition_api_id && _isNotEmpty(animal.study_group));

    openModal('MAP_ANIMALS', {
      animals,
      plannedAnimals: unmappedPlannedAnimals,
      groups: groupsData,
      onSubmit: async (mappedAnimals: AnimalMapV1) => {
        try {
          await ApiService.call({
            endpoint: 'POST /api/v1/studies/{studyApiId}/planned/animals/map-animals',
            path: { studyApiId: study?.api_id },
            body: mappedAnimals,
            options: { onError: { toast: false } },
          });
          successToast('Successfully mapped planned animals');
          await fetchPlannedAnimals?.();
          setRefetchToken(uuid());
        } catch (err) {
          errorToast('Could not map planned animals');
          ExceptionHandler.captureException(
            new InVivoError('Could not map planned animals', {
              cause: err,
              slug: 'map-planned-animals',
            })
          );
        }
      },
    });
  }, [animalsData, selectedAnimalDefinitions, unmappedPlannedAnimals, groupsData, study, fetchPlannedAnimals]);

  const isMapAnimalsDisabled = useMemo(() => {
    const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
    const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;
    const hasNoAnimals = animalsData.length === 0;

    return hasNoUnmappedPlannedAnimals || hasNoAnimals || hasReviewOutstanding;
  }, [unmappedPlannedAnimals, animalsData, settings.studyState?.pageStates?.animals]);

  const mapAnimalsTooltip = useMemo(() => {
    const hasReviewOutstanding = settings.studyState?.pageStates?.animals;
    const hasNoUnmappedPlannedAnimals = unmappedPlannedAnimals.length === 0;
    const hasNoAnimals = animalsData.length === 0;

    if (hasReviewOutstanding) {
      return 'Pending review must be completed';
    }
    if (hasNoUnmappedPlannedAnimals) {
      return 'No planned animals available';
    }
    if (hasNoAnimals) {
      return 'No registered animals available';
    }
    return undefined;
  }, [unmappedPlannedAnimals, animalsData, settings.studyState?.pageStates?.animals]);

  return (
    <div className="pb4" data-test-component="PlannedAnimalsTable" data-test-element="planned-animals-table-container">
      {loading ? (
        <div style={{ height: 650 }}>
          <Loading txt="Loading planned animals..." />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
          {apiError ? (
            <ApiErrorBanner
              className="mw6 mb4"
              error={apiError}
              title="There was an error fetching animals data"
              text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
            />
          ) : null}
          <APITable
            apiInfo={{
              type: 'internalApi',
              route: RouteService.api({
                endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
                path: { studyApiId: study?.api_id },
              }).url,
            }}
            includeParam="study_group"
            columns={columns}
            bulkActions={bulkActions}
            defaultSortBy={{ id: 'planned_id', desc: false }}
            settings={updatedSettings}
            reduxTableName="planned_animals"
            showMetricChangeFrom={false}
            pageSizes={[50, 100, 200, 500, 1000]}
            defaultPageSize={1000}
            filterOptions={filterOptions}
            refetch={refetchToken}
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(metadataOptions ?? [])}
            onSelectRow={onRowSelectionChange}
            AsideComponent={() => (
              <>
                {_isNotEmpty(groupsError) && (
                  <div style={{ display: 'flex' }}>
                    <RiAlertFill className="red" size={25} />
                    <span className="pl2">{groupsError}</span>
                  </div>
                )}
                {hasAccess && (
                  <Button
                    onClick={handleMapPlannedAnimals}
                    disabled={isMapAnimalsDisabled}
                    tooltip={mapAnimalsTooltip}
                    testId="map-animals-button"
                  >
                    Map animals
                  </Button>
                )}
              </>
            )}
          />
        </>
      )}
    </div>
  );
};

export default PlannedAnimalsTable;
